import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { buildOpportunityReference, formatDisplayReference } from "@/data/opportunityTypes";
import { toUuidOrNull } from "@/lib/uuid";
import type { DbQuoteUpdate, DbQuote } from "@/types/database";

type QuoteWithLinkedJob = { linked_job_id?: string | null };
type QuoteReadSource = "quotes_with_items_view" | "quotes_view" | "quotes";
const QUOTES_CACHE_TTL_MS = 30_000;
const QUOTE_SELECT_COLUMNS = [
  "id",
  "organization_id",
  "customer_id",
  "job_id",
  "quote_number",
  "quote_name",
  "customer_name",
  "customer_email",
  "customer_phone",
  "customer_address",
  "lead_source",
  "appointment_id",
  "status",
  "project_config",
  "notes",
  "total_estimate",
  "scope_of_work",
  "verification",
  "scenarios",
  "pricing_guardrails",
  "pricing_override",
  "pricing_status",
  "deposit",
  "lost_reason",
  "locked_at",
  "locked_by",
  "presented_at",
  "won_at",
  "lost_at",
  "contract",
  "created_by",
  "updated_by",
  "created_at",
  "updated_at",
  "lead_id",
  "linked_job_id",
  "branch_id",
  "stage_history",
].join(", ");
const QUOTE_OPPORTUNITY_HYDRATION_COLUMNS = [
  "id",
  "organization_id",
  "customer_id",
  "job_id",
  "quote_number",
  "quote_name",
  "customer_name",
  "customer_email",
  "customer_phone",
  "customer_address",
  "lead_source",
  "appointment_id",
  "status",
  "notes",
  "total_estimate",
  "lost_reason",
  "presented_at",
  "won_at",
  "lost_at",
  "created_by",
  "updated_by",
  "created_at",
  "updated_at",
  "lead_id",
  "linked_job_id",
  "branch_id",
].join(", ");
let quotesAllCache: { at: number; data: unknown; error: unknown } | null = null;
let quotesAllInFlight: Promise<{ data: unknown; error: unknown }> | null = null;
let quotesOpportunityHydrationCache: { at: number; data: unknown; error: unknown } | null = null;
let quotesOpportunityHydrationInFlight: Promise<{ data: unknown; error: unknown }> | null = null;

function invalidateQuoteCaches() {
  quotesAllCache = null;
  quotesAllInFlight = null;
  quotesOpportunityHydrationCache = null;
  quotesOpportunityHydrationInFlight = null;
}

export type QuoteUpsertPayload = Partial<DbQuoteUpdate> & {
  stage?: string | null;
};

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function applyNormalizedQuoteFinancials(target: Record<string, unknown>) {
  const hasDirectFinancials =
    target.subtotal !== undefined ||
    target.tax !== undefined ||
    target.discount !== undefined ||
    target.total !== undefined;
  if (hasDirectFinancials) return;

  const config =
    target.project_config && typeof target.project_config === "object" && !Array.isArray(target.project_config)
      ? (target.project_config as Record<string, unknown>)
      : null;
  const pricing =
    config?.pricing && typeof config.pricing === "object" && !Array.isArray(config.pricing)
      ? (config.pricing as Record<string, unknown>)
      : null;

  const subtotal =
    readNumber(pricing?.subtotal) ??
    readNumber(config?.subtotal) ??
    readNumber(target.total_estimate);
  const tax = readNumber(pricing?.tax) ?? readNumber(config?.tax) ?? 0;
  const discount = readNumber(pricing?.discount) ?? readNumber(config?.discount) ?? 0;
  const total =
    readNumber(pricing?.total) ??
    readNumber(config?.total) ??
    (subtotal != null ? subtotal + tax - discount : readNumber(target.total_estimate));

  if (subtotal != null) target.subtotal = subtotal;
  if (tax != null) target.tax = tax;
  if (discount != null) target.discount = discount;
  if (total != null) target.total = total;
}

function buildAddressFromContext(ctx: Record<string, unknown>): string | null {
  const line1 = readString(ctx.addressLine1) ?? readString(ctx.address);
  const city = readString(ctx.city);
  const state = readString(ctx.state);
  const zip = readString(ctx.zip);

  if (!line1 && !city && !state && !zip) return null;
  return [line1, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

function normalizeQuoteWritePayload(input: QuoteUpsertPayload): QuoteUpsertPayload {
  const normalized: QuoteUpsertPayload = { ...input };
  applyNormalizedQuoteFinancials(normalized as unknown as Record<string, unknown>);

  // Map stage to status if stage is provided (quotes uses status column, not stage)
  if (normalized.stage !== undefined) {
    normalized.status = normalized.stage;
    delete normalized.stage; // Remove stage - quotes table doesn't have this column
  }

  const rawConfig = normalized.project_config;
  if (!rawConfig || typeof rawConfig !== "object" || Array.isArray(rawConfig)) {
    return normalized;
  }

  const config = rawConfig as Record<string, unknown>;
  const leadContext = (
    config.leadContext && typeof config.leadContext === "object" && !Array.isArray(config.leadContext)
      ? (config.leadContext as Record<string, unknown>)
      : null
  );
  if (!leadContext) return normalized;

  const fallbackFullName = [readString(leadContext.firstName), readString(leadContext.lastName)]
    .filter(Boolean)
    .join(" ")
    .trim();
  const fullName =
    readString(leadContext.fullName) ??
    readString(leadContext.customerName) ??
    readString(leadContext.primaryName) ??
    readString(leadContext.name) ??
    (fallbackFullName.length > 0 ? fallbackFullName : null);

  const email =
    readString(leadContext.primaryEmail) ??
    readString(leadContext.customerEmail) ??
    readString(leadContext.email);

  const phone =
    readString(leadContext.primaryPhone) ??
    readString(leadContext.customerPhone) ??
    readString(leadContext.phone);

  const address = buildAddressFromContext(leadContext);

  const leadSource =
    readString(leadContext.leadSource) ??
    readString(leadContext.sourceName) ??
    readString(leadContext.source);

  if (normalized.customer_name == null && fullName) normalized.customer_name = fullName;
  if (normalized.customer_email == null && email) normalized.customer_email = email;
  if (normalized.customer_phone == null && phone) normalized.customer_phone = phone;
  if (normalized.customer_address == null && address) normalized.customer_address = address;
  if (normalized.lead_source == null && leadSource) normalized.lead_source = leadSource;

  return normalized;
}

function isLikelyJobNumber(value: string): boolean {
  return /^JOB-\d+$/i.test(value.trim());
}

async function normalizeLinkedJobIdsForRead<T extends QuoteWithLinkedJob>(rows: T[]): Promise<T[]> {
  const linkedIds = Array.from(
    new Set(
      rows
        .map((row) => row.linked_job_id)
        .filter((linkedJobId): linkedJobId is string => (
          typeof linkedJobId === "string" &&
          linkedJobId.length > 0 &&
          !isLikelyJobNumber(linkedJobId)
        )),
    ),
  );

  if (!linkedIds.length) {
    return rows;
  }

  const { data: jobs, error } = await supabase
    .from("jobs")
    .select("id, job_number")
    .in("id", linkedIds);

  if (error || !jobs?.length) {
    if (error) {
      console.warn("[QuoteService] Failed to normalize linked_job_id values:", error);
    }
    return rows;
  }

  const jobNumberById = new Map(
    jobs.map((job) => [job.id, job.job_number ?? job.id]),
  );

  return rows.map((row) => ({
    ...row,
    linked_job_id: row.linked_job_id ? (jobNumberById.get(row.linked_job_id) ?? row.linked_job_id) : row.linked_job_id,
  }));
}

async function resolveLinkedJobIdForWrite(linkedJobId: string | null | undefined): Promise<string | null | undefined> {
  if (!linkedJobId) return linkedJobId;
  if (!isLikelyJobNumber(linkedJobId)) return linkedJobId;

  const { data, error } = await supabase
    .from("jobs")
    .select("id")
    .eq("job_number", linkedJobId)
    .maybeSingle();

  if (error) {
    console.warn(`[QuoteService] Failed to resolve job number ${linkedJobId} to job UUID:`, error);
    return linkedJobId;
  }

  return data?.id ?? linkedJobId;
}

async function getCurrentUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) {
    throw new Error("Authenticated user is required to create quotes");
  }
  return user.id;
}

function isValidOpportunityReference(value: string | null | undefined): boolean {
  return typeof value === "string" && /^OPP-\d+$/i.test(value.trim());
}

function extractOpportunitySequence(value: string | null | undefined): number | null {
  if (!isValidOpportunityReference(value)) return null;
  const [, raw] = value!.trim().toUpperCase().split("-");
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function isQuoteNumberConflict(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? String((error as { code?: unknown }).code ?? "") : "";
  const message = "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
  return code === "23505" && message.includes("idx_quotes_org_quote_number_unique");
}

function isMissingReadSourceError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? String((error as { code?: unknown }).code ?? "") : "";
  return code === "PGRST205";
}

function extractMissingQuotesColumn(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const code = "code" in error ? String((error as { code?: unknown }).code ?? "") : "";
  const message = "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
  if (code !== "42703") return null;
  const match = message.match(/column\s+quotes\.([a-zA-Z0-9_]+)\s+does not exist/i);
  return match?.[1] ?? null;
}

function removeSelectColumn(selectColumns: string, column: string): string {
  const parts = selectColumns
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part !== column);
  return parts.join(", ");
}

async function queryQuotesReadSource<T>(
  run: (source: QuoteReadSource) => Promise<{ data: T | null; error: unknown }>,
): Promise<{ data: T | null; error: unknown }> {
  // quotes table first — it has all columns (lead_id, quote_number, etc.).
  // Views may exist in Supabase without these columns and won't trigger a
  // PGRST205, so they'd silently win and break the lead→quote merge.
  const sources: QuoteReadSource[] = ["quotes", "quotes_view", "quotes_with_items_view"];
  let lastMissingError: unknown = null;

  for (const source of sources) {
    const result = await run(source);
    if (!result.error) return result;
    if (isMissingReadSourceError(result.error)) {
      lastMissingError = result.error;
      continue;
    }
    return result;
  }

  return { data: null, error: lastMissingError };
}

async function generateOpportunityQuoteReference(organizationId: string): Promise<string> {
  const { data, error } = await supabase
    .from("quotes")
    .select("quote_number")
    .eq("organization_id", organizationId);

  if (error) {
    throw new Error(`Unable to generate quote reference: ${error.message}`);
  }

  const maxSeq = (data ?? []).reduce((max, row) => {
    const current = extractOpportunitySequence((row as { quote_number?: string | null }).quote_number) ?? 0;
    return Math.max(max, current);
  }, 0);

  return buildOpportunityReference(maxSeq + 1);
}

async function insertQuoteWithConflictRetry(
  row: Record<string, unknown>,
  organizationId: string,
  explicitQuoteNumber?: string | null,
) {
  const providedQuoteNumber = isValidOpportunityReference(explicitQuoteNumber)
    ? formatDisplayReference(explicitQuoteNumber)
    : null;

  const maxAttempts = providedQuoteNumber ? 1 : 5;
  let lastError: unknown = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const quoteNumber = providedQuoteNumber ?? await generateOpportunityQuoteReference(organizationId);
    const { data, error } = await supabase
      .from("quotes")
      .insert([{ ...row, organization_id: organizationId, quote_number: quoteNumber }])
      .select()
      .single();

    if (!error && data) return { data, error: null };

    lastError = error;
    if (!isQuoteNumberConflict(error) || providedQuoteNumber) {
      return { data: null, error };
    }
  }

  return { data: null, error: lastError };
}

/**
 * QuoteService provides data access to the quotes table in Supabase
 */
export const QuoteService = {
  /**
   * Lightweight quote rows for lead/appointment/opportunity list hydration.
   * Avoids large quote-detail blobs such as contract, scenarios, and project_config.
   */
  async getOpportunityHydrationRows(options?: { forceRefresh?: boolean }) {
    const forceRefresh = options?.forceRefresh === true;
    if (forceRefresh) {
      quotesOpportunityHydrationCache = null;
      quotesOpportunityHydrationInFlight = null;
    }

    const now = Date.now();
    if (
      !forceRefresh &&
      quotesOpportunityHydrationCache &&
      now - quotesOpportunityHydrationCache.at < QUOTES_CACHE_TTL_MS
    ) {
      return {
        data: quotesOpportunityHydrationCache.data,
        error: quotesOpportunityHydrationCache.error,
      };
    }
    if (!forceRefresh && quotesOpportunityHydrationInFlight) {
      const inFlight = await quotesOpportunityHydrationInFlight;
      return { data: inFlight.data, error: inFlight.error };
    }

    const request = (async () => {
      const { data, error } = await queryQuotesReadSource<any[]>(async (source) => {
        const response = await supabase
          .from(source)
          .select(QUOTE_OPPORTUNITY_HYDRATION_COLUMNS)
          .order("created_at", { ascending: false });
        return { data: response.data, error: response.error };
      });

      quotesOpportunityHydrationCache = { at: Date.now(), data, error };
      return { data, error };
    })();

    quotesOpportunityHydrationInFlight = request;
    try {
      return await request;
    } finally {
      quotesOpportunityHydrationInFlight = null;
    }
  },

  /**
   * Get all quotes from Supabase - mimics Supabase's return format { data, error }
   */
  async getAll(options?: { forceRefresh?: boolean }) {
    const forceRefresh = options?.forceRefresh === true;
    if (forceRefresh) {
      invalidateQuoteCaches();
    }
    const now = Date.now();
    if (!forceRefresh && quotesAllCache && now - quotesAllCache.at < QUOTES_CACHE_TTL_MS) {
      return { data: quotesAllCache.data, error: quotesAllCache.error };
    }
    if (!forceRefresh && quotesAllInFlight) {
      const inFlight = await quotesAllInFlight;
      return { data: inFlight.data, error: inFlight.error };
    }

    const request = (async () => {
    let selectColumns = QUOTE_SELECT_COLUMNS;
    let data: any[] | null = null;
    let error: unknown = null;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const result = await queryQuotesReadSource<any[]>(async (source) => {
        const response = await supabase
          .from(source)
          .select(selectColumns)
          .order("created_at", { ascending: false });
        return { data: response.data, error: response.error };
      });
      data = result.data;
      error = result.error;

      if (!error) break;

      const missingColumn = extractMissingQuotesColumn(error);
      if (!missingColumn || !selectColumns.split(",").map((c) => c.trim()).includes(missingColumn)) {
        break;
      }

      console.warn(`[QuoteService] Retrying quote read without missing column: ${missingColumn}`);
      selectColumns = removeSelectColumn(selectColumns, missingColumn);
    }

    if (error || !data?.length) {
      quotesAllCache = { at: Date.now(), data, error };
      return { data, error };
    }

    const normalizedData = await normalizeLinkedJobIdsForRead(data);

    const leadIds = Array.from(
      new Set(
        normalizedData
          .map((quote) => quote.lead_id)
          .filter((leadId): leadId is string => typeof leadId === "string" && leadId.length > 0),
      ),
    );

    if (!leadIds.length) {
      quotesAllCache = { at: Date.now(), data: normalizedData, error: null };
      return { data: normalizedData, error: null };
    }

    const { data: leads, error: leadsError } = await supabase
      .from("leads")
      .select("id, stage, lead_source, source_name, lead_source_id")
      .in("id", leadIds);

    if (leadsError || !leads?.length) {
      quotesAllCache = { at: Date.now(), data: normalizedData, error: leadsError };
      return { data: normalizedData, error: leadsError };
    }

    // Resolve lead_source_id → name for leads that only have the FK
    const unresolvedLeads = leads.filter((l) => !l.lead_source && !l.source_name && l.lead_source_id);
    let leadSourceNameById = new Map<string, string>();
    if (unresolvedLeads.length) {
      const sourceIds = [...new Set(unresolvedLeads.map((l) => l.lead_source_id as string))];
      const { data: sources } = await supabase
        .from("lead_sources")
        .select("id, name")
        .in("id", sourceIds);
      if (sources) {
        leadSourceNameById = new Map(sources.map((s) => [s.id, s.name]));
      }
    }

    const leadStageById = new Map(leads.map((lead) => [lead.id, lead.stage]));
    const leadSourceById = new Map(leads.map((lead) => [
      lead.id,
      lead.lead_source || lead.source_name || (lead.lead_source_id ? leadSourceNameById.get(lead.lead_source_id) : undefined),
    ]));
    const mergedData = normalizedData.map((quote) => ({
      ...quote,
      status: quote.lead_id ? leadStageById.get(quote.lead_id) || quote.status : quote.status,
      lead_source: quote.lead_source || (quote.lead_id ? leadSourceById.get(quote.lead_id) : undefined) || quote.lead_source,
    }));

      const payload = { data: mergedData, error: null };
      quotesAllCache = { at: Date.now(), data: payload.data, error: payload.error };
      return payload;
    })();
    quotesAllInFlight = request;
    try {
      return await request;
    } finally {
      quotesAllInFlight = null;
    }
  },

  /**
   * Get quotes by status
   */
  async getByStatus(status: string) {
    const { data, error } = await queryQuotesReadSource<any[]>(async (source) => {
      const { data, error } = await supabase
        .from(source)
        .select("*")
        .eq("status", status)
        .order("created_at", { ascending: false });
      return { data, error };
    });

    if (error || !data?.length) {
      return { data, error };
    }

    return { data: await normalizeLinkedJobIdsForRead(data), error: null };
  },

  /**
   * Get a single quote by ID
   */
  async getById(id: string) {
    const { data, error } = await queryQuotesReadSource<any>(async (source) => {
      const { data, error } = await supabase
        .from(source)
        .select("*")
        .eq("id", id)
        .maybeSingle();
      return { data, error };
    });

    if (error || !data) {
      return { data, error };
    }

    const [normalized] = await normalizeLinkedJobIdsForRead([data]);

    // If the quote doesn't have lead_source, resolve it from the linked lead → lead_sources lookup
    if (!normalized.lead_source && normalized.lead_id) {
      const { data: lead } = await supabase
        .from("leads")
        .select("lead_source, source_name, lead_source_id")
        .eq("id", normalized.lead_id)
        .maybeSingle();
      if (lead) {
        if (lead.lead_source || lead.source_name) {
          normalized.lead_source = lead.lead_source || lead.source_name;
        } else if (lead.lead_source_id) {
          const { data: src } = await supabase
            .from("lead_sources")
            .select("name")
            .eq("id", lead.lead_source_id)
            .maybeSingle();
          if (src?.name) normalized.lead_source = src.name;
        }
      }
    }

    return { data: normalized, error: null };
  },

  /**
   * Create a new quote - mimics Supabase's return format { data, error }
   * Auto-populates created_by from auth context if not provided
   */
  async create(quoteData: {
    quote_name: string;
    quote_number?: string | null;
    customer_name: string;
    customer_email: string | null;
    customer_phone: string | null;
    customer_address: string | null;
    lead_source?: string | null;
    status: string;
    notes: string | null;
    total_estimate: number;
    customer_id?: string;
    organization_id?: string;
    updated_by?: string;
    appointment_id?: string | null;
    lead_id?: string | null;
    linked_job_id?: string | null;
    project_config?: Record<string, unknown> | null;
    subtotal?: number | null;
    tax?: number | null;
    discount?: number | null;
    total?: number | null;
    verification?: Record<string, unknown> | null;
    pricing_guardrails?: Record<string, unknown> | null;
    pricing_override?: Record<string, unknown> | null;
    pricing_status?: string | null;
    deposit?: Record<string, unknown> | null;
    contract?: Record<string, unknown> | null;
    scope_of_work?: string | null;
    created_by?: string;
    scenarios?: unknown[];
  }) {
    const currentUserId = await getCurrentUserId();
    const createdBy = toUuidOrNull(quoteData.created_by) ?? currentUserId;
    const updatedBy = toUuidOrNull(quoteData.updated_by) ?? createdBy;
    const organizationId = quoteData.organization_id || await getCurrentOrganizationId();
    const linkedJobId = await resolveLinkedJobIdForWrite(quoteData.linked_job_id);
    if (!quoteData.customer_id) {
      throw new Error("customer_id is required when creating a quote");
    }

    const normalizedQuoteName = quoteData.quote_name?.trim() || quoteData.customer_name.trim();
    const normalizedCreatePayload: Record<string, unknown> = { ...quoteData };
    applyNormalizedQuoteFinancials(normalizedCreatePayload);
    
    const { data, error } = await insertQuoteWithConflictRetry(
      {
        ...normalizedCreatePayload,
        linked_job_id: linkedJobId ?? null,
        quote_name: normalizedQuoteName,
        customer_id: quoteData.customer_id,
        created_by: createdBy,
        updated_by: updatedBy,
        branch_id: null,
      },
organizationId,
       quoteData.quote_number,
     );

     if (!error) invalidateQuoteCaches();
     return { data: data as DbQuote | null, error };
   },

   /**
    * Update an existing quote
   */
  async update(id: string, updates: QuoteUpsertPayload) {
    const normalizedUpdates: Record<string, unknown> = {
      ...normalizeQuoteWritePayload(updates),
    };
    if (Object.prototype.hasOwnProperty.call(updates, "linked_job_id")) {
      normalizedUpdates.linked_job_id = await resolveLinkedJobIdForWrite(
        updates.linked_job_id as string | null | undefined,
      );
    }

const { data, error } = await supabase
       .from("quotes")
       .update(normalizedUpdates)
       .eq("id", id)
       .select()
       .maybeSingle();

     if (!error) invalidateQuoteCaches();
     return { data: data as DbQuote | null, error };
   },

  /**
   * Upsert a quote by lead_id — creates if none exists, updates if one does
   */
  async upsertByLeadId(leadId: string, updates: QuoteUpsertPayload) {
    const normalizedInput = normalizeQuoteWritePayload(updates);
    // Check if a quote already exists for this lead
    const { data: existing } = await supabase
      .from("quotes")
      .select("id")
      .eq("lead_id", leadId)
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      return QuoteService.update(existing.id, normalizedInput);
    }

    // Create new quote linked to this lead
    const [createdBy, organizationId] = await Promise.all([
      getCurrentUserId(),
      getCurrentOrganizationId(),
    ]);
    const updatedBy =
      typeof normalizedInput.updated_by === "string"
        ? (toUuidOrNull(normalizedInput.updated_by) ?? createdBy)
        : createdBy;
    const linkedJobId = await resolveLinkedJobIdForWrite(normalizedInput.linked_job_id);

    const { data, error } = await insertQuoteWithConflictRetry(
      {
        ...normalizedInput,
        linked_job_id: linkedJobId ?? null,
        lead_id: leadId,
        created_by: createdBy,
        updated_by: updatedBy,
        branch_id: null,
      },
      organizationId,
      typeof normalizedInput.quote_number === "string" ? normalizedInput.quote_number : null,
    );

if (!error) invalidateQuoteCaches();
     return { data: data as DbQuote | null, error };
   },

   /**
    * Get latest quote for a lead_id (most recently updated/created).
   */
  async getLatestByLeadId(leadId: string) {
    const { data, error } = await queryQuotesReadSource<any>(async (source) => {
      const { data, error } = await supabase
        .from(source)
        .select("*")
        .eq("lead_id", leadId)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return { data, error };
    });

    if (error || !data) {
      return { data, error };
    }

    const [normalized] = await normalizeLinkedJobIdsForRead([data]);
    return { data: normalized, error: null };
  },

  /**
   * Delete a quote
   */
  async delete(id: string) {
    const { error } = await supabase
      .from("quotes")
      .delete()
      .eq("id", id);

    if (!error) invalidateQuoteCaches();
    return { error };
  },

  /**
   * Get line items for a quote
   */
  async getLineItemsByQuoteId(quoteId: string) {
    const { data, error } = await supabase
      .from("quote_items")
      .select("*")
      .eq("quote_id", quoteId)
      .order("sort_order", { ascending: true });

    return { data, error };
  },

  /**
   * Create a line item for a quote
   */
  async createLineItem(item: {
    quote_id: string;
    item_type?: string;
    description: string;
    quantity?: number;
    unit_price?: number;
    sort_order?: number;
    metadata?: Record<string, unknown>;
  }) {
    const { data, error } = await supabase
      .from("quote_items")
      .insert([item])
      .select()
      .single();

    return { data, error };
  },

  /**
   * Update a line item
   */
  async updateLineItem(id: string, updates: {
    item_type?: string;
    description?: string;
    quantity?: number;
    unit_price?: number;
    sort_order?: number;
    metadata?: Record<string, unknown>;
  }) {
    const { data, error } = await supabase
      .from("quote_items")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    return { data, error };
  },

  /**
   * Delete a line item
   */
  async deleteLineItem(id: string) {
    const { error } = await supabase
      .from("quote_items")
      .delete()
      .eq("id", id);

    return { error };
  },
};
